import React, { useContext, useState } from 'react';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import env from '@lib/envs/env';
import SectionToggle
  from '@components/pages/DesignerProfile/DesignerProfileAdmin/SectionToggle/SectionToggle';
import UserContext from '@context/User/user-context';
import { IUser } from '@models/User/i-user';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import Loader from '@components/shared/Loader/Loader';
import styles from './DesignerProfile.module.scss';
import DesignerIntro from './DesignerIntro/DesignerIntro';
import DesignerDesigns from './DesignerDesigns/DesignerDesigns';
import DesignerBio from './DesignerBio/DesignerBio';
import PreferButton from './PreferButton/PreferButton';
import useBookDesignerAndProceedToPurchase from './PreferButton/utils';

interface IDesignerJsonLdSchema {
  '@context': 'http://schema.org/';
  '@type': 'Person';
  familyName: string;
  givenName: string;
  worksFor: 'Havenly';
  workLocation: string;
  jobTitle: 'Interior Designer';
  knowsAbout: 'Interior Design';
  Url: string;
}
interface IDesignerProfileProps {
  designer: IDesignerProfile; // todo: define an interface
  initialBoards: IDesignerBoard[];
}

const SECTION_DESIGNS = 'designs';
const SECTION_PROFILE = 'profile';

const DesignerProfile = ({
  designer,
  initialBoards,
}: IDesignerProfileProps) => {
  const sections = [
    { id: SECTION_DESIGNS, text: `${designer.firstName}’s Designs` },
    { id: SECTION_PROFILE, text: 'Profile Info' }
  ];
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [editing, setEditing] = useState(false);

  const { user } = useContext(UserContext);

  const {
    bookDesignerAndProceedToPurchase,
    isChangingDesigner
  } = useBookDesignerAndProceedToPurchase();

  const bookDesigner = async () => {
    await bookDesignerAndProceedToPurchase(designer);
  };

  const allowEdit = shouldAllowEdit(designer.id, user);

  const toggleEditing = () => { setEditing(!editing); };

  const schemaData: IDesignerJsonLdSchema = {
    '@context': 'http://schema.org/',
    '@type': 'Person',
    familyName: designer.lastName,
    givenName: designer.firstName,
    worksFor: 'Havenly',
    workLocation: designer.location || '',
    jobTitle: 'Interior Designer',
    knowsAbout: 'Interior Design',
    Url: `https://havenly.com${designer.profileUri}`
  };

  return (
    <>
      {
        isChangingDesigner && (
          <div className={styles.LoadingLogo}>
            <Loader />
          </div>
        )
      }

      <DefaultTemplate
        canonicalLink={`https://${env.DOMAIN_NAME}${designer.profileUri}`}
        title={`${designer.firstName} ${designer.lastName} - Interior Designer | Havenly`}
        description={designerDescription(designer)}
        jsonSchema={schemaData}
      >
        <div className={styles.Root}>
          <DesignerIntro
            designer={designer}
            allowEdit={allowEdit}
            editing={editing}
            toggleEditing={toggleEditing}
          />
          <SectionToggle
            sections={sections}
            activeSection={activeSection}
            onSectionChange={(section) => setActiveSection(section)}
          />

          <DesignerDesigns
            className={activeSection === SECTION_DESIGNS ? '' : styles.SectionHidden}
            designer={designer}
            editing={editing}
            initialBoards={initialBoards}
          />

          <DesignerBio
            designer={designer}
            className={activeSection === SECTION_PROFILE ? '' : styles.SectionHidden}
          />

          <PreferButton
            allowEdit={ allowEdit }
            designer={ designer }
            editing={ editing }
            toggleEditing={toggleEditing}
            bookDesigner={bookDesigner}
          />
        </div>
      </DefaultTemplate>
    </>
  );
};

export default DesignerProfile;

function designerDescription(designer: IDesignerProfile) {
  let description =
    `${designer.firstName} ${designer.lastName} is a Havenly Interior Designer`;
  if (designer.location) {
    description += ` from ${designer.location}`;
  }
  description += '.';

  if (designer.favoriteSubStyles?.length) {
    description += ` ${designer.firstName} specializes in `;
    const styleCount = Math.min(designer.favoriteSubStyles.length, 3);
    for (let i = 0; i < styleCount - 1; i += 1) {
      description += `${(i > 0) ? ', ' : ''}${designer.favoriteSubStyles[i].name}`;
    }
    if (styleCount > 1) {
      description += ` and ${designer.favoriteSubStyles[styleCount - 1].name}`;
    }

    description += ' interior design styles.';
  }

  return description;
}

function shouldAllowEdit(designerId: number | string, user: IUser | undefined | null) {
  return user?.role === 'admin' ||
    (user?.role === 'designer' && Number(user.designerInfo?.id) === Number(designerId));
}
